import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppShell } from "../design-system/layouts/AppShell";
import { SplitPanel } from "../design-system/layouts/SplitPanel";
import { PageTabs } from "../design-system/primitives/PageTabs";
import { Button } from "../design-system/primitives/Button";
import { IconButton } from "../design-system/primitives/IconButton";
import { AxonMark } from "../design-system/primitives/AxonMark";
import { AboutButton } from "../features/about/AboutButton";
import { WorkspaceBanner } from "../features/brand/WorkspaceBanner";
import { SignalsTable } from "../features/signals-table/SignalsTable";
import { IedPanel } from "../features/ied-panel/IedPanel";
import { useNbStore } from "../store/nbStore";
import { usePagesStore } from "../store/pagesStore";
import { useLayoutStore } from "../store/layoutStore";
import { useUiStore } from "../store/uiStore";
import { LAYOUT_TEMPLATES } from "../lib/jsonContracts";
import type { NbSignal } from "../lib/jsonContracts";
import { exportSignalsCsv, exportSignalsJson, exportSignalsXlsx, exportAG800 } from "../lib/exporters";
import { exportCorporateXlsx, type CorpTab } from "../lib/corporateExporter";
import { parseSignalList } from "../lib/referenceParsers";
import { predictBatch, validateInScl } from "../wasm/client";

export function WorkspacePage() {
  const { t } = useTranslation("workspace");
  const { pages, activeId, activate, close, rename, open, reset:resetPages } = usePagesStore();
  const { getTab, setTab, clearResults, validateExistingRows, addRows, removeTab } = useNbStore();
  const { layoutId, setLayout } = useLayoutStore();
  const { theme, toggleTheme }  = useUiStore();

  // C1 FIX: usar activeId directamente como tabId (nunca "default")
  const tabId     = activeId ?? "";
  const tabState  = getTab(tabId);
  const { rows, sclNodes, selectedIed, status } = tabState;

  const sclInputRef   = useRef<HTMLInputElement>(null);
  const extraInputRef = useRef<HTMLInputElement>(null);
  const [dlOpen,   setDlOpen]   = useState(false);
  const activeTitle = pages.find(p => p.id === activeId)?.title ?? "axon-tags";
  const [dlName, setDlName] = useState("");
  // Modal de seleccion de pestanas para exportacion corporativa
  const [corpOpen, setCorpOpen] = useState(false);
  const [corpSel, setCorpSel]   = useState<Set<string>>(new Set());
  // Sincronizar dlName con el título de la pestaña al abrir el dropdown
  const openDl = () => { setDlName(activeTitle); setDlOpen(v => !v); };

  // Abrir el modal corporativo: por defecto todas las pestañas seleccionadas
  const openCorpModal = () => {
    setCorpSel(new Set(pages.map(p => p.id)));
    setCorpOpen(true);
    setDlOpen(false);
  };

  // Ejecutar la exportación corporativa con las pestañas elegidas
  const runCorpExport = async () => {
    const tabs: CorpTab[] = pages
      .filter(p => corpSel.has(p.id))
      .map(p => {
        const st = getTab(p.id);
        return { id: p.id, title: p.title, ied: st.selectedIed,
                 rows: st.rows } as CorpTab;
      })
      .filter(t => t.rows.length > 0);
    if (tabs.length === 0) { alert(t("corp.selectAtLeastOne")); return; }
    await exportCorporateXlsx(tabs, dlName || activeTitle);
    setCorpOpen(false);
  };
  const hasSCL        = sclNodes.length > 0;
  const tpl           = LAYOUT_TEMPLATES.find(t=>t.id===layoutId) ?? LAYOUT_TEMPLATES[0];
  const goHome        = () => { resetPages(); };

  const handleWsSclFile = async (file: File) => {
    if (!tabId) return;
    await validateExistingRows(tabId, new Uint8Array(await file.arrayBuffer()));
  };

  const handleExtraFile = async (file: File) => {
    if (!tabId) return;
    const bytes = new Uint8Array(await file.arrayBuffer());
    const ext   = file.name.toLowerCase();
    if (/\.(scd|cid|icd|iid|scl|xml)$/.test(ext)) {
      await validateExistingRows(tabId, bytes);
    } else {
      const parsed  = await parseSignalList(bytes, file.name);
      const newRows = await predictBatch(parsed.map(p=>p.signal), selectedIed);
      const final   = sclNodes.length ? await validateInScl(newRows, sclNodes) : newRows;
      addRows(tabId, final);
    }
  };

  // C7+8+9: actualizar filas directamente en el tab
  const handleRowsChange = (newRows: NbSignal[]) => {
    if (!tabId) return;
    setTab(tabId, { rows: newRows });
  };

  // C8+9: inferir una señal nueva e insertarla en la posición indicada
  const handleInferSignal = async (signal: string, insertAt: number) => {
    if (!tabId) return;
    const [newRow] = await predictBatch([signal], selectedIed);
    const finalRow = sclNodes.length ? (await validateInScl([newRow], sclNodes))[0] : newRow;
    const newRows  = [...rows];
    newRows.splice(insertAt, 0, finalRow);
    setTab(tabId, { rows: newRows });
  };

  // C5 FIX: título de nueva pestaña con IED
  const handleNewTab = () => {
    const id    = `tab-${Date.now()}`;
    const title = selectedIed ? t("tabs.signalsIed", { ied: selectedIed }) : t("tabs.newTitle");
    open({ id, title, kind:"results" });
  };

  return (
    <AppShell
      topBar={
        <div className="flex h-9 items-center gap-3 px-3">
          <AxonMark />
          <span className="text-sm font-semibold text-[var(--color-brand)]">{t("appName")}</span>
          <div className="ml-auto flex items-center gap-1.5">
            <select value={layoutId} onChange={(e)=>setLayout(e.target.value as typeof layoutId)}
              className="h-7 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-xs text-[var(--color-on-surface)] outline-none">
              {LAYOUT_TEMPLATES.map(tpl=><option key={tpl.id} value={tpl.id}>{t(`layouts.${tpl.id}`)}</option>)}
            </select>
            <IconButton
              icon={theme==="dark"?"light_mode":"dark_mode"}
              aria-label={theme==="dark"?t("topbar.themeToLight"):t("topbar.themeToDark")}
              title={theme==="dark"?t("topbar.themeLightTitle"):t("topbar.themeDarkTitle")}
              onClick={toggleTheme}
            />
            <IconButton icon="close" aria-label={t("topbar.closeAria")}
              title={t("topbar.closeTitle")} onClick={goHome} />
            <span className="mx-1 h-5 w-px bg-[var(--color-border)]" />
            <AboutButton />
          </div>
        </div>
      }
      statusBar={
        <div className="flex gap-4">
          <span>{t("status.signals")} <b>{rows.length}</b></span>
          {hasSCL && <span>{t("status.present")} <b>{rows.filter(r=>r.status==="present").length}</b></span>}
          {hasSCL && <span>{t("status.absent")}  <b>{rows.filter(r=>r.status==="absent").length}</b></span>}
          {status==="loading" && <span className="text-[var(--color-brand)]">{t("status.processing")}</span>}
          {tabState.error && <span className="text-[var(--color-status-absent)] text-xs">{tabState.error}</span>}
        </div>
      }
    >
      <div className="flex h-full flex-col">
        <WorkspaceBanner />
        {/* C2 FIX: Tabs + botón + junto a la última pestaña */}
        <div className="flex items-stretch border-b border-[var(--color-border)] bg-[var(--color-surface-container-low)] overflow-x-auto">
          <PageTabs
            tabs={pages} activeId={activeId}
            onActivate={activate}
            onClose={id => { close(id); removeTab(id); }}
            onRename={rename}
          />
          <button onClick={handleNewTab}
            className="shrink-0 self-center mx-1 w-6 h-6 flex items-center justify-center rounded border border-[var(--color-border)] text-[var(--color-on-surface-variant)] hover:bg-[var(--color-surface-variant)] hover:text-[var(--color-on-surface)] text-sm leading-none"
            title={t("tabs.newAria")}>+</button>
        </div>

        {/* Barra de acciones */}
        <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-surface-container-low)] px-3 py-1.5">
          <input ref={extraInputRef} type="file" accept=".xlsx,.xls,.csv,.txt,.scd,.cid,.icd,.iid,.scl,.xml"
            className="hidden" onChange={e=>{ const f=e.target.files?.[0]; if(f) void handleExtraFile(f); }} />
          <Button variant="ghost" onClick={()=>{
            if(extraInputRef.current){ extraInputRef.current.value=""; extraInputRef.current.click(); }
          }} className="py-1 px-2 text-xs border border-[var(--color-border)]">
            {t("actions.add")}
          </Button>
          <Button variant="ghost" onClick={()=>clearResults(tabId)} className="py-1 px-2 text-xs border border-[var(--color-border)]">{t("actions.clear")}</Button>
          {/* Botón Descargar con dropdown — click-outside para cerrarlo */}
          <div className="relative">
            <Button variant="ghost"
              onClick={openDl}
              className="py-1 px-2 text-xs border border-[var(--color-border)] flex items-center gap-1">
              ⬇ {t("actions.download")} <span className="text-[9px]">▾</span>
            </Button>
            {dlOpen && (
              <>
                {/* Overlay transparente para cerrar al clic fuera */}
                <div className="fixed inset-0 z-40" onClick={() => setDlOpen(false)} />
                <div className="absolute left-0 top-full z-50 mt-0.5 rounded border border-[var(--color-border)] bg-[var(--color-surface-container)] shadow-lg py-2 min-w-[320px]">
                  {/* Campo de nombre editable */}
                  <div className="px-3 pb-2 border-b border-[var(--color-outline-variant)]">
                    <label className="block text-[10px] text-[var(--color-on-surface-variant)] mb-1 uppercase tracking-wide">
                      {t("actions.fileNameLabel")}
                    </label>
                    <input
                      value={dlName}
                      onChange={e => setDlName(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      onKeyDown={e => e.stopPropagation()}
                      className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-xs text-[var(--color-on-surface)] outline-none focus:border-[var(--color-brand)]"
                      placeholder={t("actions.fileNamePlaceholder")}
                    />
                  </div>
                  {/* Opciones de formato */}
                  <div className="pt-1">
                    {[
                      { key:"csv",   label:"CSV",   ext:"csv",  fn:()=>void exportSignalsCsv(rows, dlName || activeTitle) },
                      { key:"json",  label:"JSON",  ext:"json", fn:()=>void exportSignalsJson(rows, dlName || activeTitle) },
                      { key:"xlsx",  label:"XLSX",  ext:"xlsx", fn:()=>void exportSignalsXlsx(rows, dlName || activeTitle) },
                      { key:"corp",  label:t("download.corporate"), ext:"xlsx", fn:()=>openCorpModal() },
                      { key:"ag800", label:"AG800", ext:"xlsx", fn:()=>void exportAG800(rows, dlName || activeTitle) },
                    ].map(item => (
                      <button key={item.key}
                        onClick={(e) => { e.stopPropagation(); item.fn(); setDlOpen(false); }}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-[var(--color-on-surface)] hover:bg-[var(--color-surface-variant)] transition-colors">
                        <span className="font-mono text-[var(--color-brand)] w-32 shrink-0 whitespace-nowrap">{item.label}</span>
                        <span className="text-[var(--color-on-surface-variant)] truncate text-[10px]">
                          {(dlName || activeTitle).substring(0, 24)}{((dlName || activeTitle).length > 24) ? "…" : ""}.{item.ext}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {selectedIed && <span className="text-xs text-[var(--color-on-surface-variant)]">{t("iedLabel")} <b className="text-[var(--color-brand)]">{selectedIed}</b></span>}
            <input ref={sclInputRef} type="file" accept=".scd,.cid,.icd,.iid,.scl,.xml" className="hidden"
              onChange={e=>{ const f=e.target.files?.[0]; if(f) void handleWsSclFile(f); }} />
            <Button variant="outline" onClick={()=>{
              if(sclInputRef.current){ sclInputRef.current.value=""; sclInputRef.current.click(); }
            }} className="py-1 px-2 text-xs">
              {hasSCL ? t("actions.validateSclDone") : t("actions.validateScl")}
            </Button>
          </div>
        </div>

        {/* Contenido */}
        <div className="min-h-0 flex-1">
          <SplitPanel direction={tpl.direction} autoSaveId={`axon-tags-transformer.split.${tpl.id}`}
            defaultSizes={tpl.defaultSizes}
            first={
              <SignalsTable
                rows={rows}
                selectedIed={selectedIed}
                sclNodes={sclNodes}
                onRowsChange={handleRowsChange}
                onInferSignal={handleInferSignal}
              />
            }
            second={<IedPanel tabId={tabId} sclDisabled={hasSCL} />}
          />
        </div>
      </div>

      {/* ── Modal de selección de pestañas para exportación corporativa ── */}
      {corpOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50"
          onClick={() => setCorpOpen(false)}>
          <div className="w-[420px] max-h-[80vh] overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-container)] shadow-xl"
            onClick={e => e.stopPropagation()}>
            <div className="border-b border-[var(--color-border)] px-4 py-3">
              <h3 className="text-sm font-semibold text-[var(--color-brand)]">{t("corp.title")}</h3>
              <p className="mt-0.5 text-[11px] text-[var(--color-on-surface-variant)]">
                {t("corp.subtitle")}
              </p>
            </div>
            <div className="max-h-[45vh] overflow-y-auto px-4 py-2">
              {pages.length === 0 && (
                <p className="py-4 text-center text-xs text-[var(--color-on-surface-variant)]">{t("corp.empty")}</p>
              )}
              {pages.map(p => {
                const st = getTab(p.id);
                const checked = corpSel.has(p.id);
                return (
                  <label key={p.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-[var(--color-surface-variant)]">
                    <input type="checkbox" checked={checked}
                      onChange={() => setCorpSel(prev => {
                        const n = new Set(prev);
                        if (n.has(p.id)) n.delete(p.id); else n.add(p.id);
                        return n;
                      })}
                      className="accent-[var(--color-brand)]" />
                    <span className="flex-1 truncate text-xs">{p.title}</span>
                    <span className="text-[10px] text-[var(--color-on-surface-variant)]">
                      {t("corp.signalsCount", { count: st.rows.length })}{st.selectedIed ? ` · ${st.selectedIed}` : ""}
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-[var(--color-border)] px-4 py-3">
              <div className="flex gap-2 text-[11px]">
                <button onClick={() => setCorpSel(new Set(pages.map(p => p.id)))}
                  className="text-[var(--color-brand)] hover:underline">{t("corp.all")}</button>
                <button onClick={() => setCorpSel(new Set())}
                  className="text-[var(--color-on-surface-variant)] hover:underline">{t("corp.none")}</button>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setCorpOpen(false)} className="text-xs">{t("corp.cancel")}</Button>
                <Button variant="primary" onClick={() => void runCorpExport()} className="text-xs">{t("corp.export")}</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
